import { useState } from "react";
import { api } from "../lib/api";
import { Check, Crown, Heart, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { BreadcrumbJsonLd } from "../components/JsonLd";
import { FaqSection } from "../components/FaqSection";

const FAQ_ITEMS = [
  {
    q: "Comment devenir membre de l'association Fedevo 2.0 ?",
    a: "Choisissez l'<strong>Adhésion Association à 5 €</strong>, cliquez sur <em>Devenir membre</em>, puis validez le paiement sur Stripe. Vous recevez un email de confirmation et devenez membre officiel de la Fédération Vaudaise du Hip-Hop et des Cultures Urbaines.",
  },
  {
    q: "Le paiement est-il sécurisé ?",
    a: "Oui. Tous les paiements passent par <strong>Stripe Checkout</strong> (norme PCI DSS niveau 1). Fedevo 2.0 ne stocke jamais vos coordonnées bancaires : seul Stripe les voit, et c'est chiffré de bout en bout.",
  },
  {
    q: "Quelle est la différence entre l'Adhésion, le Pack Créateur et le Pack Premium ?",
    a: "<strong>Adhésion 5 €</strong> : soutien à l'association + badge membre fondateur. <strong>Pack Créateur 19 € / mois</strong> : inclut tout + téléversement jusqu'à <strong>25 vidéos/mois</strong> sur la chaîne Fedevo 2.0 TV. <strong>Pack Premium 99 € / mois</strong> : uploads illimités, mise en avant Trending, stats détaillées, logo dans le bandeau partenaires.",
  },
  {
    q: "Comment téléverser mes vidéos après achat ?",
    a: "Après le paiement, un <strong>jeton d'upload</strong> vous est affiché et sauvegardé dans votre navigateur. Rendez-vous sur votre <a href='/creator'>Espace créateur</a> (<code>/creator</code>) → formulaire « Téléverser une vidéo » et le quota se décompte automatiquement à chaque envoi. Aucun mot de passe admin n'est nécessaire.",
  },
  {
    q: "Les vidéos YouTube comptent-elles dans mon quota ?",
    a: "Non. Le quota ne s'applique qu'aux <strong>fichiers MP4 téléversés</strong> sur notre serveur. Vous pouvez ajouter autant de vidéos YouTube que vous voulez via le formulaire « Ajouter une URL » ou l'« Import multiple YouTube ».",
  },
  {
    q: "Puis-je annuler mon abonnement à tout moment ?",
    a: "Oui. Les packs Créateur et Premium sont des paiements ponctuels de 30 jours — il n'y a <strong>pas de prélèvement automatique</strong>. À la fin de la période, votre quota s'épuise et vous n'êtes débité que si vous choisissez explicitement de renouveler.",
  },
  {
    q: "Comment devenir partenaire ou sponsor ?",
    a: "Contactez RBY Concept (copyright holder & sponsor principal) ou envoyez un message via le formulaire newsletter — nous revenons vers vous sous 48h avec nos grilles de visibilité.",
  },
  {
    q: "Comment accéder à l'Espace créateur ?",
    a: "Ouvrez <a href='/creator'>/creator</a> et collez votre jeton d'upload (affiché après le paiement). Vous y voyez votre quota restant, la date d'expiration et le formulaire de téléversement avec miniature.",
  },
  {
    q: "Où est hébergé le site et les vidéos ?",
    a: "Fedevo 2.0 est hébergé sur infrastructure cloud européenne. Les vidéos sont stockées sur object storage sécurisé, conformément au RGPD. Aucune donnée personnelle n'est revendue à des tiers.",
  },
];

const PACKS = [
  {
    id: "membership_5",
    label: "Adhésion Association",
    price: "5 €",
    period: "Paiement unique",
    icon: Heart,
    color: "#14a3a3",
    perks: [
      "Membre officiel de l'asso Fedevo 2.0",
      "Soutien direct à la street culture",
      "Badge Membre fondateur (30 premiers jours)",
      "Accès aux événements partenaires",
    ],
    cta: "Devenir membre",
  },
  {
    id: "creator_19",
    label: "Pack Créateur",
    price: "19 €",
    period: "30 jours",
    icon: Upload,
    color: "#14a3a3",
    perks: [
      "Tout ce qu'inclut l'adhésion",
      "Téléversement jusqu'à 25 vidéos / mois sur la chaîne Fedevo 2.0 TV",
      "Jeton d'upload personnel sécurisé",
      "Accès à votre Espace créateur (/creator)",
    ],
    cta: "Passer Créateur",
  },
  {
    id: "premium_99",
    label: "Pack Premium",
    price: "99 €",
    period: "30 jours",
    icon: Crown,
    color: "#f0b429",
    featured: true,
    perks: [
      "Tout ce qu'inclut le Pack Créateur",
      "Uploads illimités sur la chaîne Fedevo 2.0 TV",
      "Mise en avant Trending 24h après publication",
      "Statistiques détaillées de votre audience",
      "Logo affiché dans le bandeau partenaires",
    ],
    cta: "Passer Premium",
  },
];

export default function Membership() {
  const [loading, setLoading] = useState(null);

  const buy = async (id) => {
    setLoading(id);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: id,
        origin_url: window.location.origin,
      });
      window.location.assign(data.url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de paiement");
      setLoading(null);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-14">
      <BreadcrumbJsonLd items={[{ name: "Accueil", path: "/" }, { name: "Adhésion", path: "/membership" }]} />
      <div className="mb-12 text-center">
        <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--accent)] mb-3">Rejoignez le mouvement</div>
        <h1 className="font-display text-5xl sm:text-6xl">Adhésion & Premium</h1>
        <p className="text-[color:var(--text-muted)] mt-4 max-w-2xl mx-auto">
          Soutenez Fedevo 2.0, accédez aux coulisses, et diffusez votre univers sur la chaîne urbaine du collectif.
        </p>
      </div>
      <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto stagger">
        {PACKS.map(p => {
          const Icon = p.icon;
          const isLoading = loading === p.id;
          return (
            <div
              key={p.id}
              data-testid={`pack-${p.id}`}
              className={`relative rounded-2xl p-8 border bg-[color:var(--bg-elev)] ${p.featured ? 'border-[color:var(--accent)] shadow-[0_24px_60px_-20px_rgba(240,180,41,0.3)]' : 'border-[color:var(--border)]'}`}
            >
              {p.featured && (
                <span className="absolute top-4 right-4 px-3 py-1 bg-[color:var(--accent)] text-black text-[10px] uppercase tracking-widest font-bold rounded-full">
                  Recommandé
                </span>
              )}
              <Icon className="w-10 h-10 mb-4" style={{ color: p.color }} />
              <div className="font-display text-3xl">{p.label}</div>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="font-display text-5xl text-[color:var(--accent)]">{p.price}</span>
                <span className="text-xs uppercase tracking-widest text-[color:var(--text-muted)]">{p.period}</span>
              </div>
              <ul className="mt-6 space-y-2.5">
                {p.perks.map((perk) => (
                  <li key={`${p.id}-${perk}`} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: p.color }} />
                    <span>{perk}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => buy(p.id)}
                disabled={isLoading}
                data-testid={`buy-${p.id}`}
                className={`mt-7 w-full py-3 rounded-full font-semibold text-sm uppercase tracking-widest transition disabled:opacity-50 inline-flex items-center justify-center gap-2 ${p.featured ? 'bg-[color:var(--accent)] text-black hover:brightness-110' : 'border border-[color:var(--border)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)]'}`}
              >
                {isLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirection...</> : p.cta}
              </button>
            </div>
          );
        })}
      </div>
      <p className="text-center text-xs text-[color:var(--text-muted)] mt-10">
        Paiement sécurisé via Stripe · Annulation à tout moment · Aucune donnée bancaire stockée par Fedevo 2.0
      </p>
      <FaqSection items={FAQ_ITEMS} />
    </div>
  );
}
